import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import {
  Plus, Trash2, X, ChevronDown, ChevronUp, Upload, Download, Edit, FileQuestion, Eye,
} from 'lucide-react';
import { Q_TYPES } from './constants';
import { PreviewTab } from './PreviewTab';

export function QuestionsTab({ sections, setSections, eventRef, form }) {
  const [importMode, setImportMode] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Notify parent to skip next auto-save cycle after import (prevent race condition)
  const triggerImmediateSave = async (newSections) => {
    setSections(newSections);
    if (eventRef) {
      try {
        await api.post(`/api/rfx/events/${eventRef}/autosave`, { sections: newSections });
      } catch { /* will retry via normal auto-save */ }
    }
  };

  const addSection = () => {
    setSections([...sections, { section_name: `Section ${sections.length + 1}`, questions: [] }]);
  };

  const updateSection = (idx, field, value) => {
    const updated = [...sections];
    updated[idx] = { ...updated[idx], [field]: value };
    setSections(updated);
  };

  const removeSection = (idx) => {
    if (!window.confirm('Remove this section and all its questions?')) return;
    setSections(sections.filter((_, i) => i !== idx));
  };

  const addQuestion = (sectionIdx) => {
    const updated = [...sections];
    updated[sectionIdx].questions.push({
      question_id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      text: '', type: 'free_text', options: [], weight: 1, max_score: 10, required: true,
    });
    setSections(updated);
  };

  const updateQuestion = (sIdx, qIdx, field, value) => {
    const updated = [...sections];
    updated[sIdx].questions[qIdx] = { ...updated[sIdx].questions[qIdx], [field]: value };
    setSections(updated);
  };

  const removeQuestion = (sIdx, qIdx) => {
    const updated = [...sections];
    updated[sIdx].questions.splice(qIdx, 1);
    setSections(updated);
  };

  const moveQuestion = (sIdx, qIdx, dir) => {
    const updated = [...sections];
    const qs = updated[sIdx].questions;
    const newIdx = qIdx + dir;
    if (newIdx < 0 || newIdx >= qs.length) return;
    [qs[qIdx], qs[newIdx]] = [qs[newIdx], qs[qIdx]];
    setSections(updated);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
      processFile(file);
    } else if (file) {
      toast.error('Please drop an Excel (.xlsx, .xls) or CSV file');
    }
  };

  const processFile = async (file) => {
    setImportLoading(true);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);
      setImportData(rows);
      setImportMode(true);
      toast.success(`${rows.length} row(s) parsed from ${file.name}`);
    } catch (err) {
      toast.error('Failed to parse spreadsheet: ' + err.message);
    }
    setImportLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
    e.target.value = '';
  };

  const confirmImport = async (mode) => {
    if (!importData?.length) return;
    if (eventRef) {
      try {
        const r = await api.post(`/api/rfx/events/${eventRef}/import-questions`, { rows: importData, mode });
        toast.success(r.data.message);
        // Reload sections from DB and force-save to parent state
        const ev = await api.get(`/api/rfx/events/${eventRef}`);
        const loaded = ev.data.sections || [];
        setSections(loaded);
        toast.success('Questionnaire saved');
      } catch (e) { toast.error(getApiErrorMessage(e, 'Import failed')); }
    } else {
      const sectionsMap = {};
      for (const row of importData) {
        const secName = row.Section || row.section || 'General';
        if (!sectionsMap[secName]) sectionsMap[secName] = { section_name: secName, questions: [] };
        const qText = row.Question || row.question || '';
        if (!qText) continue;
        let qType = (row.Type || row.type || 'free_text').toLowerCase().replace(/\s/g, '_');
        const validTypes = ['yes_no', 'multiple_choice', 'free_text', 'numeric', 'file_upload', 'date', 'rating'];
        if (!validTypes.includes(qType)) qType = 'free_text';
        const optsRaw = row.Options || row.options || row['Options (comma-separated)'] || '';
        const sep = optsRaw.includes('|') ? '|' : ',';
        const options = qType === 'multiple_choice' ? optsRaw.split(sep).map(o => o.trim()).filter(Boolean) : [];
        sectionsMap[secName].questions.push({
          question_id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          text: qText, type: qType, options,
          weight: parseFloat(row.Weight || row.weight || 1) || 1,
          max_score: parseFloat(row['Max Score'] || row.max_score || 10) || 10,
          required: ['yes', 'true', '1', 'y'].includes(String(row.Required || row.required || 'yes').toLowerCase()),
        });
      }
      const newSections = Object.values(sectionsMap);
      const merged = mode === 'replace' ? newSections : [...sections, ...newSections];
      // Save immediately to prevent auto-save race condition
      await triggerImmediateSave(merged);
      toast.success(`${importData.length} question(s) imported and saved`);
    }
    setImportData(null);
    setImportMode(false);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/api/rfx/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'rfx_questions_template.xlsx'; a.click();
    } catch { toast.error('Failed to download template'); }
  };

  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);

  return (
    <div className="space-y-4 py-2">
      {/* Header with context explanation */}
      <div className="bg-slate-50 border rounded-lg p-3">
        <p className="text-sm text-slate-700 font-medium">Questionnaire Builder</p>
        <p className="text-xs text-slate-500 mt-0.5">Build questions that suppliers must answer as part of their response. Use questions to assess capability, compliance, experience, technical fit, and any other criteria important to your evaluation. Questions can be organised into sections and imported from a spreadsheet for efficiency. For RFPs, assign scoring weights to each question to enable weighted evaluation.</p>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{sections.length} section(s), {totalQuestions} question(s)</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Download Template
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportMode(!importMode)}
            className={importMode ? 'bg-indigo-50 border-indigo-300' : ''} data-testid="import-toggle-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> Import from Spreadsheet
          </Button>
          <Button size="sm" onClick={addSection} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-section-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Section
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowPreview(!showPreview)}
            disabled={totalQuestions === 0}
            className={showPreview ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : ''}
            data-testid="preview-questionnaire-btn">
            <Eye className="w-3.5 h-3.5 mr-1" /> Preview
          </Button>
        </div>
      </div>

      {importMode && (
        <div
          onDrop={handleFileDrop}
          onDragOver={e => e.preventDefault()}
          className="border-2 border-dashed border-indigo-300 rounded-lg p-4 bg-indigo-50/50"
        >
          <h3 className="font-medium text-sm mb-2">Import Questions from Spreadsheet</h3>
          {!importData && (
            <>
              <div
                onClick={() => { const i = document.createElement('input'); i.type = 'file'; i.accept = '.xlsx,.xls,.csv'; i.onchange = handleFileUpload; i.click(); }}
                className="border-2 border-dashed border-indigo-200 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-100/30 transition-colors mb-2"
              >
                <Upload className="w-6 h-6 mx-auto mb-2 text-indigo-400" />
                <p className="text-sm font-medium text-slate-700">Drag & drop Excel or CSV file here</p>
                <p className="text-xs text-slate-400 mt-1">or click to browse — Columns: Section, Question, Type, Options, Weight, Max Score, Required</p>
              </div>
              {importLoading && <p className="text-sm text-indigo-600">Parsing...</p>}
            </>
          )}
          {importData && (
            <div className="mt-1">
              <p className="text-sm font-medium text-emerald-700 mb-2">{importData.length} row(s) ready to import</p>
              <div className="max-h-48 overflow-y-auto border rounded bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>{Object.keys(importData[0] || {}).map(k => <th key={k} className="px-2 py-1 text-left font-medium">{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {importData.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t">{Object.values(row).map((v, j) => <td key={j} className="px-2 py-1 truncate max-w-[200px]">{String(v)}</td>)}</tr>
                    ))}
                    {importData.length > 10 && <tr><td colSpan={99} className="px-2 py-1 text-slate-400">...and {importData.length - 10} more rows</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={() => confirmImport('append')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="import-append-btn">Append to Existing</Button>
                <Button size="sm" variant="outline" onClick={() => confirmImport('replace')} data-testid="import-replace-btn">Replace All Questions</Button>
                <Button size="sm" variant="ghost" onClick={() => { setImportData(null); setImportMode(false); }}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {sections.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileQuestion className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No questions yet. Add a section or import from a spreadsheet.</p>
        </div>
      ) : (
        sections.map((section, sIdx) => (
          <SectionBlock key={sIdx} section={section} sIdx={sIdx}
            updateSection={updateSection} removeSection={removeSection}
            addQuestion={addQuestion} updateQuestion={updateQuestion}
            removeQuestion={removeQuestion} moveQuestion={moveQuestion} />
        ))
      )}

      {/* Inline Preview — shown as overlay above the content */}
      {showPreview && totalQuestions > 0 && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-8" onClick={() => setShowPreview(false)}
          role="dialog" aria-modal="true" aria-label="Supplier Questionnaire Preview"
          onKeyDown={e => { if (e.key === 'Escape') setShowPreview(false); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-emerald-50 px-5 py-3 flex items-center justify-between border-b border-emerald-200 z-10">
              <span className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5"><Eye className="w-4 h-4" /> Supplier Preview — How suppliers will see this questionnaire</span>
              <Button size="sm" variant="ghost" onClick={() => setShowPreview(false)} className="h-7 text-xs text-emerald-700 hover:bg-emerald-100">Close</Button>
            </div>
            <PreviewTab sections={sections} form={form || {}} />
          </div>
        </div>
      )}
    </div>
  );
}

function SectionBlock({ section, sIdx, updateSection, removeSection, addQuestion, updateQuestion, removeQuestion, moveQuestion }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`section-${sIdx}`}>
      <div className="bg-slate-100 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1">
          <button onClick={() => setCollapsed(!collapsed)} className="text-slate-400 hover:text-slate-600" aria-label={collapsed ? 'Expand section' : 'Collapse section'}>
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          <Input value={section.section_name} onChange={e => updateSection(sIdx, 'section_name', e.target.value)}
            className="h-7 text-sm font-semibold bg-transparent border-none focus:bg-white max-w-[400px]"
            data-testid={`section-name-${sIdx}`} />
          <span className="text-xs text-slate-400">{section.questions.length} question(s)</span>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => addQuestion(sIdx)} className="h-7 px-2 text-indigo-600"
            data-testid={`add-question-${sIdx}`}><Plus className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" onClick={() => removeSection(sIdx)} className="h-7 px-2 text-red-400 hover:text-red-600">
            <Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {!collapsed && (
        <div className="p-3 space-y-2">
          {section.questions.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">No questions in this section.
              <button onClick={() => addQuestion(sIdx)} className="text-indigo-600 ml-1 underline">Add one</button></p>
          ) : (
            section.questions.map((q, qIdx) => (
              <QuestionRow key={q.question_id || qIdx} q={q} sIdx={sIdx} qIdx={qIdx}
                updateQuestion={updateQuestion} removeQuestion={removeQuestion} moveQuestion={moveQuestion}
                isFirst={qIdx === 0} isLast={qIdx === section.questions.length - 1} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function QuestionRow({ q, sIdx, qIdx, updateQuestion, removeQuestion, moveQuestion, isFirst, isLast }) {
  const [expanded, setExpanded] = useState(!q.text);
  const typeInfo = Q_TYPES.find(t => t.value === q.type) || Q_TYPES[0];
  const TypeIcon = typeInfo.icon;

  return (
    <div className="border rounded p-3 bg-white hover:shadow-sm transition-shadow" data-testid={`question-${sIdx}-${qIdx}`}>
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-0.5 mt-1">
          <button onClick={() => moveQuestion(sIdx, qIdx, -1)} disabled={isFirst}
            className="text-slate-300 hover:text-slate-500 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
          <button onClick={() => moveQuestion(sIdx, qIdx, 1)} disabled={isLast}
            className="text-slate-300 hover:text-slate-500 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
        </div>
        <span className="text-xs text-slate-400 font-mono mt-2 w-6">Q{qIdx + 1}</span>
        <div className="flex-1 space-y-2">
          <Input value={q.text} onChange={e => updateQuestion(sIdx, qIdx, 'text', e.target.value)}
            placeholder="Enter your question..." className="text-sm h-8"
            data-testid={`question-text-${sIdx}-${qIdx}`} />
          {(expanded || !q.text) && (
            <div className="flex flex-wrap items-center gap-2">
              <Select value={q.type} onValueChange={v => updateQuestion(sIdx, qIdx, 'type', v)}>
                <SelectTrigger className="w-[160px] h-7 text-xs" data-testid={`question-type-${sIdx}-${qIdx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Q_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Label className="text-xs">Weight:</Label>
                <Input type="number" value={q.weight} onChange={e => updateQuestion(sIdx, qIdx, 'weight', parseFloat(e.target.value) || 1)}
                  className="w-14 h-7 text-xs" />
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-xs">Max Score:</Label>
                <Input type="number" value={q.max_score} onChange={e => updateQuestion(sIdx, qIdx, 'max_score', parseFloat(e.target.value) || 10)}
                  className="w-14 h-7 text-xs" />
              </div>
              <div className="flex items-center gap-1.5">
                <Checkbox checked={q.required} onCheckedChange={c => updateQuestion(sIdx, qIdx, 'required', c)}
                  data-testid={`question-required-${sIdx}-${qIdx}`} />
                <Label className="text-xs">Required</Label>
              </div>
            </div>
          )}
          {q.type === 'multiple_choice' && (expanded || !q.text) && (
            <OptionsEditor options={q.options || []} onChange={opts => updateQuestion(sIdx, qIdx, 'options', opts)} />
          )}
        </div>
        <div className="flex items-center gap-1">
          <Badge className="text-[9px] bg-slate-100 text-slate-500"><TypeIcon className="w-3 h-3 mr-0.5 inline" />{typeInfo.label}</Badge>
          <button onClick={() => setExpanded(!expanded)} className="text-slate-400 hover:text-slate-600 p-1" aria-label={expanded ? 'Collapse question details' : 'Expand question details'}>
            <Edit className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => removeQuestion(sIdx, qIdx)} className="text-red-300 hover:text-red-500 p-1" aria-label="Remove question">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function OptionsEditor({ options, onChange }) {
  const addOption = () => onChange([...options, '']);
  const updateOption = (idx, val) => { const u = [...options]; u[idx] = val; onChange(u); };
  const removeOption = (idx) => onChange(options.filter((_, i) => i !== idx));

  return (
    <div className="ml-2 space-y-1">
      <Label className="text-xs text-slate-500">Answer Options (multi-select checkboxes):</Label>
      {options.map((opt, i) => (
        <div key={i} className="flex items-center gap-1">
          <Checkbox disabled className="opacity-40" />
          <Input value={opt} onChange={e => updateOption(i, e.target.value)} placeholder={`Option ${i + 1}`}
            className="h-7 text-xs flex-1" data-testid={`option-${i}`} />
          <button onClick={() => removeOption(i)} className="text-red-300 hover:text-red-500" aria-label={`Remove option ${i + 1}`}><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={addOption} className="h-6 text-xs text-indigo-600">
        <Plus className="w-3 h-3 mr-1" /> Add Option
      </Button>
    </div>
  );
}
